import type { StageId } from '../types'

/** The bid-stage model, unchanged from the prototype. */
export const STAGES: { id: StageId; label: string; hint: string }[] = [
  { id: 'discover', label: 'Discover', hint: 'Brief generated — research the account' },
  { id: 'assess', label: 'Assess', hint: 'Meeting held — capture notes' },
  { id: 'qualify', label: 'Qualify', hint: 'Follow-up & go/no-go decision' },
  { id: 'pipeline', label: 'Pipeline', hint: "Live opportunity — it's a go" },
]

export const stageIndex = (id: StageId | null | undefined): number => {
  const i = STAGES.findIndex((s) => s.id === id)
  return i < 0 ? 0 : i
}

export const stageLabel = (id: StageId | null | undefined): string => STAGES[stageIndex(id)].label

export const bandColor = (score: number | null | undefined): string => {
  const s = score ?? 0
  return s >= 70 ? 'var(--good)' : s >= 45 ? 'var(--warm)' : 'var(--cool)'
}

export const bandFromScore = (score: number | null | undefined): string => {
  const s = score ?? 0
  return s >= 70 ? 'Hot' : s >= 45 ? 'Warm' : 'Watch'
}

/** Strips protocol/www/path, returning a bare domain — or '' if it doesn't look like one. */
export function cleanDomain(web: string | null | undefined): string {
  const d = (web ?? '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .replace(/\?.*$/, '')
  return /\.[a-z]{2,}$/.test(d) ? d : ''
}

/** Company names entered as a URL ("tesco.com") render as "Tesco". */
export function displayName(company: string): string {
  let n = (company ?? '').trim()
  if (/^https?:\/\//i.test(n) || /^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(n)) {
    n = n
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/.*$/, '')
      .split('.')[0]
    n = n.charAt(0).toUpperCase() + n.slice(1)
  }
  return n
}

/** Deterministic colour for the initial-letter fallback badge, so a company is always the same hue. */
export function logoBadgeColor(name: string): string {
  const palette = ['#8C4189', '#EF5974', '#28A0DD', '#0753AD', '#D1D146', '#E1539D', '#06325E']
  let h = 0
  for (let i = 0; i < (name ?? '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return palette[h % palette.length]
}

const CAP_SHORT: Record<string, string> = {
  'Product Strategy & Delivery Readiness': 'Product Strategy',
  'Accelerated Software Delivery': 'Software Delivery',
  'Intelligent Data & AI': 'Data & AI',
  'Secure Cloud & Platform Engineering': 'Cloud & Platform',
}
export const capShort = (c: string | undefined): string => (c ? (CAP_SHORT[c] ?? c) : '')

/** Maps the model's free-text influence label onto one of the four badge styles. */
export function inflClass(influence: string | undefined): string {
  const i = (influence ?? '').toLowerCase()
  if (i.startsWith('econ')) return 'econ'
  if (i.startsWith('champ')) return 'champ'
  if (i.startsWith('gate')) return 'gate'
  return 'infl'
}

export const CONF_COLOUR: Record<string, string> = {
  high: 'var(--good)',
  medium: 'var(--warm)',
  low: 'var(--cool)',
}

export const tierLabel = (t: number): string => (t === 4 ? 'Approved' : `Tier ${t}`)

export const tierColor = (t: number): string =>
  ({ 1: 'var(--accent)', 2: 'var(--cool)', 3: '#8C4189', 4: 'var(--slate)' })[t] ?? 'var(--slate)'

export const TIER_NAME: Record<number, string> = {
  1: 'Major groups & household names',
  2: 'Mid-market & specialists',
  3: 'Niche, challengers & emerging',
}

/** Turns pasted notes into blocks: '-'/'*'/'•' lines become bullets, blank lines split paragraphs. */
export function parseNotes(text: string | undefined): { type: 'ul' | 'p'; items: string[] }[] {
  const blocks: { type: 'ul' | 'p'; items: string[] }[] = []
  for (const raw of (text ?? '').split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    if (/^[-*•]\s+/.test(line)) {
      const item = line.replace(/^[-*•]\s+/, '')
      const last = blocks[blocks.length - 1]
      if (last?.type === 'ul') last.items.push(item)
      else blocks.push({ type: 'ul', items: [item] })
    } else {
      blocks.push({ type: 'p', items: [line] })
    }
  }
  return blocks
}

export const formatDate = (iso?: string): string =>
  new Date(iso ?? Date.now()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
